import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useClientTeamMembership } from '@/hooks/useClientTeamMembership';
import { Calendar, LayoutDashboard, Plus, BarChart3, User, LogOut, Crown, HelpCircle, Users, UsersRound, Menu, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { NotificationBell } from '@/components/NotificationBell';

const NavigationBar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const {
    user,
    signOut
  } = useAuth();
  const { checkCanCreateEvent, subscription } = useSubscription();
  const { hasTeamMemberships, isClient } = useClientTeamMembership();
  const location = useLocation();
  const navigate = useNavigate();

  const handleCreateEvent = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    const result = checkCanCreateEvent();
    if (!result.canCreate) {
      const currentEvents = subscription.monthlyEvents || 0;
      
      toast({
        variant: "destructive",
        title: "🚫 Monthly Event Limit Reached",
        description: (
          <div className="space-y-3">
            <p className="font-medium text-base">You've created {currentEvents} events this month. Free accounts are limited to 3 events per month.</p>
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/50 dark:to-purple-950/50 p-6 rounded-xl border shadow-sm">
              <p className="font-bold text-lg text-blue-700 dark:text-blue-300 mb-2">🎉 Upgrade to Family Plan for:</p>
              <ul className="text-base font-medium text-blue-600 dark:text-blue-400 space-y-1">
                <li>• ✨ 20 events per month</li>
                <li>• 👥 Up to 100 guests per event</li>
                <li>• 🏠 Family group management</li>
                <li>• 📊 Basic analytics</li>
              </ul>
            </div>
            <p className="text-base font-medium text-muted-foreground">
              Or try Professional for unlimited events, custom branding, and advanced features!
            </p>
          </div>
        ),
        action: (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate("/subscription")}
          >
            Upgrade Now
          </Button>
        )
      });
      return;
    }
    
    navigate('/create-event');
  };
  const handleSignOut = async () => {
    await signOut();
    setIsMobileMenuOpen(false);
  };
  const navigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Create Event', href: '/create-event', icon: Plus },
    { name: 'Guest List', href: '/guest-list', icon: Users },
    { name: 'Family', href: '/family', icon: UsersRound },
    ...(hasTeamMemberships ? [{ name: isClient ? 'Client Portal' : 'Team Portal', href: '/client-dashboard', icon: Building2 }] : []),
    { name: 'Analytics', href: '/global-analytics', icon: BarChart3 },
    { name: 'Profile', href: '/profile', icon: User },
    { name: 'Subscription', href: '/subscription', icon: Crown },
    { name: 'Help', href: '/help', icon: HelpCircle },
  ];
  if (!user) return null;
  return <nav className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center space-x-2">
            <img src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" alt="Event Date Picker Logo" className="h-12 w-auto" loading="lazy" />
          </Link>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-1">
            {navigationItems.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;
            
            if (item.name === 'Create Event') {
              return (
                <button
                  key={item.name}
                  onClick={handleCreateEvent}
                  className={cn("flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors", isActive ? "bg-primary text-primary-foreground" : "hover:bg-accent hover:text-accent-foreground")}
                >
                  <Icon className="h-4 w-4" />
                  {item.name}
                </button>
              );
            }
            
            return <Link key={item.name} to={item.href} className={cn("flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors", isActive ? "bg-primary text-primary-foreground" : "hover:bg-accent hover:text-accent-foreground")}>
                  <Icon className="h-4 w-4" />
                  {item.name}
                </Link>;
          })}
          
          {/* Notification Bell */}
          <NotificationBell />
          </div>

          {/* Mobile Hamburger Menu */}
          <div className="md:hidden">
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <SheetHeader>
                  <div className="flex justify-center">
                    <img 
                      src="https://fmmcpkpxsuxsqcxfwnch.supabase.co/storage/v1/object/public/mainlogob//ChatGPT_Image_Jul_21__2025__01_34_41_PM-removebg-preview.png" 
                      alt="Event Date Picker Logo" 
                      className="h-10 w-auto" 
                      loading="lazy"
                    />
                  </div>
                </SheetHeader>
                <div className="flex flex-col space-y-2 mt-6">
                  {navigationItems.map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    
                    if (item.name === 'Create Event') {
                      return (
                        <button 
                          key={item.name}
                          onClick={(e) => {
                            setIsMobileMenuOpen(false);
                            handleCreateEvent(e);
                          }}
                          className={cn(
                            "flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors",
                            isActive 
                              ? "bg-primary text-primary-foreground" 
                              : "hover:bg-accent hover:text-accent-foreground"
                          )}
                        >
                          <Icon className="h-5 w-5" />
                          {item.name}
                        </button>
                      );
                    }
                    
                    return (
                      <Link 
                        key={item.name} 
                        to={item.href} 
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-4 py-3 rounded-lg text-base font-medium transition-colors",
                          isActive 
                            ? "bg-primary text-primary-foreground" 
                            : "hover:bg-accent hover:text-accent-foreground"
                        )}
                      >
                        <Icon className="h-5 w-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                  <div className="border-t border-border my-4" />
                  <Button 
                    variant="ghost" 
                    onClick={handleSignOut} 
                    className="flex items-center gap-3 px-4 py-3 text-base font-medium justify-start"
                  >
                    <LogOut className="h-5 w-5" />
                    Sign Out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={handleSignOut} className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:block">Sign Out</span>
            </Button>
          </div>
        </div>

      </div>
    </nav>;
};
export default NavigationBar;
